import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Landmark, Building2 } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const lines = [
  { text: "Steuerlast senken.", accent: false },
  { text: "Substanz aufbauen.", accent: true },
  { text: "Krisensicher investieren.", accent: false },
];

const badges = [
  { icon: Landmark, label: "§7i / §7h Denkmal-AfA" },
  { icon: Building2, label: "Degressive AfA & §7b Neubau" },
  { icon: ShieldCheck, label: "Börsenunabhängige Sachwerte" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative grain overflow-hidden pt-[72px]">
      <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 px-4 sm:px-8 lg:px-16 pt-16 pb-20 lg:pt-24 lg:pb-28 min-h-[92vh] items-center">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            className="eyebrow mb-8 flex items-center gap-3"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="block h-px w-10 bg-forest" />
            Steueroptimierte Sachwerte
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.12] text-petrol">
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "italic font-medium text-forest" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + i * 0.16, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subline"
            className="mt-8 max-w-xl text-base leading-relaxed text-slate-600"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            Immobilien, Photovoltaik und Kunst als strategische Bausteine Ihrer
            Vermögensarchitektur — mit Abschreibungen, Sonder-AfA und
            Gestaltungsmodellen, die Ihre Steuerlast spürbar senken.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <button data-testid="hero-cta-immobilien-check" onClick={() => scrollToId("#check")} className="btn-primary group">
              Immobilien-Check anfordern
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button data-testid="hero-cta-chapters" onClick={() => scrollToId("#immobilien")} className="btn-ghost">
              Kapitel entdecken
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-trust-badges"
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.9 }}
          >
            {badges.map((b) => (
              <span key={b.label} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                <b.icon size={14} className="text-gold" />
                {b.label}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -top-4 -left-4 h-full w-full border border-gold/50" aria-hidden="true" />
            <div className="relative aspect-[4/5] overflow-hidden">
              <motion.img
                style={{ y: imgY }}
                src="https://images.unsplash.com/photo-1758448617677-2f8bebc56d9e?fm=jpg&q=85&auto=format&fit=crop"
                alt="Moderne Kapitalanlage-Immobilie"
                className="h-[115%] w-full object-cover"
                data-testid="hero-image"
              />
            </div>

            <motion.div
              data-testid="hero-card-afa"
              className="absolute -left-6 top-10 animate-floaty bg-white/80 backdrop-blur-md border border-petrol/10 px-5 py-4 shadow-lg shadow-petrol/5"
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-forest">Sonder-AfA §7i</p>
              <p className="font-display text-2xl font-semibold text-petrol mt-1">bis zu 100&nbsp;%</p>
              <p className="text-xs text-slate-500 mt-0.5">der Sanierungskosten absetzbar</p>
            </motion.div>

            <motion.div
              data-testid="hero-card-neubau"
              className="absolute -right-4 bottom-12 animate-floaty bg-white/80 backdrop-blur-md border border-petrol/10 px-5 py-4 shadow-lg shadow-petrol/5"
              style={{ animationDelay: "1.4s" }}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.3, duration: 0.8 }}
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-forest">Neubau-AfA</p>
              <p className="font-display text-2xl font-semibold text-petrol mt-1">5 % p. a.</p>
              <p className="text-xs text-slate-500 mt-0.5">degressiv, zzgl. §7b Sonder-AfA</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
